/**
 * Google Ads purchase-conversion fire (client-side, Option A).
 *
 * Design: _bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md
 *   - AD-1: fire only on CONFIRMED payment (callers gate on lifecycle_state /
 *           invoice.status === 'paid' — never on the ?paid=1 URL flag).
 *   - Dedup: transaction_id = invoice.number (Google-side) + a per-invoice
 *            sessionStorage once-guard (stops a refresh re-firing this session;
 *            cross-session repeats are deduped by Google on transaction_id).
 *   - Consent: no fire unless the visitor accepted the 'marketing' category.
 *   - Value: invoice total in ZAR so the campaign optimises for revenue.
 *
 * Fully inert until siteConfig.googleAds is populated by the owner.
 */

import { siteConfig, readConsent, isCategoryAllowed } from '@sighthost/config';

export interface FireAdsPurchaseArgs {
  /** Invoice UUID — keys the once-guard. */
  invoiceId: string;
  /** Invoice total, ZAR (numeric). */
  value: number;
  /** Invoice number, e.g. 'INV-2026-000042' — the Google dedup transaction_id. */
  number: string;
  /** ISO currency; defaults to ZAR. */
  currency?: string;
}

export function fireAdsPurchase({
  invoiceId,
  value,
  number,
  currency = 'ZAR',
}: FireAdsPurchaseArgs): void {
  if (typeof window === 'undefined') return;

  const { conversionId, purchaseLabel } = siteConfig.googleAds;
  if (!conversionId || !purchaseLabel) return; // not configured yet
  if (!isCategoryAllowed(readConsent(), 'marketing')) return; // consent gate

  const key = `ads_fired:${invoiceId}`;
  try {
    if (window.sessionStorage.getItem(key)) return; // once per session
    window.sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage unavailable (private mode) — proceed; Google still
       dedupes on transaction_id, so at worst we send one extra ping. */
  }

  const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return;

  gtag('event', 'conversion', {
    send_to: `${conversionId}/${purchaseLabel}`,
    value,
    currency,
    transaction_id: number,
  });
}
